import type { ApiErrorCode, ErrorEnvelope } from "@guestbook/shared";

const API_BASE = "/api/v1";

/** Paths that must never trigger an auto-refresh-then-retry — these would
 *  either cause an infinite loop (the refresh endpoint itself) or are the
 *  user's intentional credential-entry path, where 401 is the answer. */
const NO_REFRESH_PATHS = new Set([
  "/auth/refresh",
  "/auth/login",
  "/auth/signup",
  // Public, unauthenticated recovery endpoints — a 4xx here is the answer,
  // not a stale-session signal, so never try to refresh-and-replay.
  "/auth/forgot-password",
  "/auth/reset-password",
]);

export class ApiClientError extends Error {
  readonly code: ApiErrorCode | "network" | "unknown";
  readonly status: number;
  readonly details: Record<string, unknown> | undefined;

  constructor(
    code: ApiErrorCode | "network" | "unknown",
    message: string,
    status: number,
    details?: Record<string, unknown>,
  ) {
    super(message);
    this.name = "ApiClientError";
    this.code = code;
    this.status = status;
    this.details = details;
  }
}

function isErrorEnvelope(value: unknown): value is ErrorEnvelope {
  return (
    typeof value === "object" &&
    value !== null &&
    "error" in value &&
    typeof (value as { error: unknown }).error === "object"
  );
}

// Module-level singleton so concurrent 401s share a single /auth/refresh
// in-flight request instead of stampeding the endpoint.
let inflightRefresh: Promise<boolean> | null = null;

// Backoff (ms) before each retry of a *transient* refresh failure. The API
// sits in front of an Aurora Serverless v2 cluster that scales to zero; the
// first request after an idle period can 500 while the cluster resumes. Rather
// than log the user out on that transient blip, retry a few times — by the
// later attempts the cluster is warm and the still-valid refresh cookie
// succeeds. Length = number of retries after the initial attempt.
const REFRESH_RETRY_BACKOFF_MS = [1_000, 3_000, 6_000];

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

type RefreshOutcome = "ok" | "auth-failed" | "transient";

async function attemptRefresh(): Promise<RefreshOutcome> {
  let res: Response;
  try {
    res = await fetch(`${API_BASE}/auth/refresh`, {
      method: "POST",
      credentials: "include",
      headers: { Accept: "application/json" },
    });
  } catch {
    // Network error — transient; a retry may land once connectivity returns.
    return "transient";
  }
  if (res.ok) return "ok";
  // A definitive auth failure: the refresh cookie is genuinely invalid or was
  // cleared server-side (reuse/expiry). Retrying can't help — log out now.
  if (res.status === 401 || res.status === 403) return "auth-failed";
  // Any other non-2xx (notably a cold-start 500) is treated as transient.
  return "transient";
}

/**
 * Issue a POST /auth/refresh, retrying transient failures (network errors and
 * 5xx, e.g. an Aurora cold-start 500) with backoff. Returns true on a 2xx,
 * false only on a definitive auth failure (401/403) or after exhausting
 * retries. Callers treat `false` as "give up and surface the 401".
 *
 * Exported so the proactive refresh timer (see useRefreshTimer) can call it
 * directly without re-entering apiFetch's retry path.
 */
export function refreshAccessToken(): Promise<boolean> {
  if (inflightRefresh) return inflightRefresh;
  inflightRefresh = (async () => {
    try {
      for (let attempt = 0; ; attempt++) {
        const outcome = await attemptRefresh();
        if (outcome === "ok") return true;
        if (outcome === "auth-failed") return false;
        // Transient: back off and retry until the schedule is exhausted.
        if (attempt >= REFRESH_RETRY_BACKOFF_MS.length) return false;
        await sleep(REFRESH_RETRY_BACKOFF_MS[attempt]!);
      }
    } finally {
      // Cleared synchronously: every caller that already entered the gate
      // above holds a reference to the same Promise; new callers after this
      // point should be free to start a fresh refresh round.
      inflightRefresh = null;
    }
  })();
  return inflightRefresh;
}

async function performFetch(url: string, init: RequestInit): Promise<Response> {
  return fetch(url, { ...init, credentials: "include" });
}

export async function apiFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  const url = `${API_BASE}${path}`;
  const hasBody = init.body !== undefined && init.body !== null;
  const headers = new Headers(init.headers);
  if (hasBody && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  headers.set("Accept", "application/json");

  const requestInit: RequestInit = { ...init, headers };

  let res: Response;
  try {
    res = await performFetch(url, requestInit);
  } catch (err) {
    throw new ApiClientError(
      "network",
      err instanceof Error ? err.message : "Network error",
      0,
    );
  }

  // Reactive refresh: if a protected request 401s, try /auth/refresh once
  // and replay the original request. The proactive timer in useRefreshTimer
  // handles the common case; this is the safety net for stale tabs and the
  // dev-restart scenario.
  if (res.status === 401 && !NO_REFRESH_PATHS.has(path)) {
    const refreshed = await refreshAccessToken();
    if (refreshed) {
      try {
        res = await performFetch(url, requestInit);
      } catch (err) {
        throw new ApiClientError(
          "network",
          err instanceof Error ? err.message : "Network error",
          0,
        );
      }
    }
    // If refresh failed (or the retry also 401'd), fall through to the
    // normal error path below — the caller's useMe()/RequireAuth handles
    // the redirect to /auth/login.
  }

  if (res.status === 204) {
    return undefined as T;
  }

  let payload: unknown = null;
  const text = await res.text();
  if (text.length > 0) {
    try {
      payload = JSON.parse(text);
    } catch {
      // Non-JSON body — leave payload null and fall through to error/return.
    }
  }

  if (!res.ok) {
    if (isErrorEnvelope(payload)) {
      throw new ApiClientError(
        payload.error.code as ApiErrorCode,
        payload.error.message,
        res.status,
        payload.error.details,
      );
    }
    throw new ApiClientError("unknown", res.statusText || "Request failed", res.status);
  }

  return payload as T;
}
